import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, categoryImages } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const textColor = scrolled
    ? 'text-foreground-700 hover:text-foreground-950'
    : 'text-background-50/90 hover:text-background-50';

  const textHoverBg = scrolled
    ? 'hover:bg-background-100'
    : 'hover:bg-background-50/15';

  const brandColor = scrolled
    ? 'text-foreground-950'
    : 'text-background-50';

  const brandAccent = scrolled
    ? 'text-primary-600'
    : 'text-primary-300';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">

          <Link to="/" className="flex items-center gap-2 group cursor-pointer" aria-label="USFansSpreadsheet Strona główna">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-stack-line text-lg md:text-xl"></i>
            </div>
            <span className={`font-heading text-base md:text-lg font-semibold whitespace-nowrap ${brandColor}`}>
              USFans<span className={brandAccent}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1.5">
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHoverBg}`}
            >
              <i className="ri-home-line mr-1.5"></i>
              Strona główna
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHoverBg}`}
            >
              <i className="ri-shopping-bag-line mr-1.5"></i>
              Przewodnik zakupowy
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHoverBg}`}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <i className="ri-grid-line"></i>
                <span>Kategorie</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto z-50 animate-[fadeIn_0.15s_ease-out]">
                  {categories.map((cat, idx) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-primary-50 transition-colors cursor-pointer border-b border-background-100 last:border-0"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <img
                        src={categoryImages[idx]}
                        alt={cat.name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHoverBg}`}
            >
              usfans spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer ml-1"
            >
              Buy It Now
            </a>
          </div>

          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${textColor} ${textHoverBg}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg animate-[slideDown_0.2s_ease-out] max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-50 text-green-600">
                <i className="ri-home-line"></i>
              </span>
              Strona główna
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <i className="ri-shopping-bag-line"></i>
              </span>
              Przewodnik zakupowy
            </a>

            <div className="pt-3 pb-1">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3 px-3">
                Kategorie produktów
              </p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, idx) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <img
                      src={categoryImages[idx]}
                      alt={cat.name}
                      className="w-full aspect-[4/3] rounded-lg object-cover"
                      loading="lazy"
                    />
                    <span className="text-xs font-medium text-foreground-700 text-center">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="pt-3 space-y-1">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600">
                  <i className="ri-external-link-line"></i>
                </span>
                usfans spreadsheet
              </a>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-cart-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}